import { isFeatureFlagEnabled, getConfig } from '../../../utilities/api';
import { checkCookie, isLoggedIn } from '../../../utilities/helpers';

/**
 * Adds the event handlers for events potentially dispatched by the application.
 */
const _addEventHandlers = (appConfig, appSlug, translator) => {
    if (!window.$wx) { return; }
    window.addEventListener(window.$wx.mfeLoginCompleteEvent?.type, () => {
        _setVisibleButtons(isLoggedIn());
    });
    window.addEventListener(
        window.$wx.mfeLogoutCompleteEvent?.type,
        async () => {
            await _setLoginLink(appConfig, appSlug, translator);
        }
    );
    window.addEventListener(window.$wx.mfeNotAuthenticatedEvent?.type, () => {
        _setVisibleButtons(false);
    });
};

/**
 * Renders the register button in the top bar.
 * @param {Translator} translator Our translation tool.
 * @returns {void} Void.
 */
const _renderRegisterButton = (translator) => {
    if (document.getElementById('register-button')) {
        return;
    }
    const registerLink = document.createElement('a');
    registerLink.setAttribute('id', 'register-button');
    registerLink.style = 'margin-right: 0;';
    if (!document.getElementById('language-select-menu')) {
        registerLink.style.marginLeft = 'auto';
    }
    registerLink.classList.add(
        'c-topbar-nav__item',
        'c-topbar-nav__link-button',
        'js-hide-when-logged-in'
    );
    registerLink.innerText = translator.translate('register');

    const loginButton = document.getElementById('login-button');
    loginButton?.parentNode.insertBefore(registerLink, loginButton);
};

/**
 * Sets the href value of the login button depending on if SSO is set up
 * for the client.
 *
 * @param {string} appSlug The app slug.
 * @param {string} appRootUrl The app root url.
 * @param {Translator} translator Our translation tool.
 * @returns {Promise<void>} A void promise indicating the work is complete.
 */
const _setLoginLink = async (appConfig, appSlug, translator) => {
    const loginButton = document.getElementById('login-button');

    if (!loginButton) {
        return;
    }

    loginButton.innerText = translator.translate('login');

    // TODO: ORCS-79 Refactor SSO so that we just send it the app slug and do not need the config at all
    const provider =
        appConfig?.authorization?.allowSsoLogin?.provider || 'okta';
    const clientSlug = appConfig?.authorization?.allowSsoLogin?.client_slug;
    const returnPath = `${window.location.protocol}//${window.location.host}/login/callback`;
    const orchestratorConfig = await getConfig();

    const ssoUrl = `${orchestratorConfig.data.SSO_URL}/${provider}/${clientSlug}/login_redirect?return_path=${returnPath}`;
    const internalUrl = '/mfe/login';

    const isSSOEnabled = await isFeatureFlagEnabled('sso', appSlug);

    loginButton.addEventListener('click', () => {
        const isQaCookieEnabled = checkCookie('qa', 'true');
        if (isSSOEnabled && !isQaCookieEnabled) {
            location.href = ssoUrl;
        } else {
            history.pushState({ path: internalUrl }, '', internalUrl);
        }
    });

    if (!isSSOEnabled) {
        _renderRegisterButton(translator);
    }
    // We need to set these after the register button has been potentially rendered.
    _setVisibleButtons(isLoggedIn());
};

/**
 * Prompts the registration modal when the Register button is clicked.
 * @returns void.
 */
const _setRegisterClickListener = () =>
    document
        .getElementById('register-button')
        ?.addEventListener('click', () => {
            const registerUrl = '/register/';
            history.pushState({ path: registerUrl }, '', registerUrl);
        });

/**
 * Sets which buttons to display on the top bar depending on the user's login status.
 * @param {boolean} isLoggedIn True if the user is logged in, false otherwise.
 */
const _setVisibleButtons = (isLoggedIn) => {
    const elementsToHide = document.getElementsByClassName(
        isLoggedIn ? 'js-hide-when-logged-in' : 'js-hide-when-logged-out'
    );

    Array.from(elementsToHide).map((element) =>
        element.classList.add('u-hide')
    );

    const elementsToShow = document.getElementsByClassName(
        isLoggedIn ? 'js-hide-when-logged-out' : 'js-hide-when-logged-in'
    );

    Array.from(elementsToShow).map((element) =>
        element.classList.remove('u-hide')
    );
};

/**
 * Renders the login and (potentially) register buttons.
 * @param {string} appSlug The app slug.
 * @param {string} appRootUrl The app root url.
 * @param {Translator} translator Our translation tool.
 * @returns {Promise<void>} A void promise indicating the work is complete.
 */
const renderLoginAndRegisterButtons = async (appConfig, appSlug, translator) => {
    await _setLoginLink(appConfig, appSlug, translator);
    _setRegisterClickListener();
    _addEventHandlers(appConfig, appSlug, translator);
}

export default renderLoginAndRegisterButtons;
